import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SteponeactivitiesComponent } from "../../../../../components/activities/steponeactivities/steponeactivities.component";
import { SteptwoactivitiesComponent } from "../../../../../components/activities/steptwoactivities/steptwoactivities.component";
import { StepthreeactivitiesComponent } from "../../../../../components/activities/stepthreeactivities/stepthreeactivities.component";
import { StepfouractivitiesComponent } from "../../../../../components/activities/stepfouractivities/stepfouractivities.component";
import { ActiviteService } from '../../../../../services/activite/activite.service';
import { routeAlias } from '../../../../../../routesalias';
import { AppService } from '../../../../../core/app.service';
import { CommonModule } from '@angular/common';
import { RecapactivitiesComponent } from "../../../../../components/activities/recapactivities/recapactivities.component";
import { toSnakeCaseUpper } from '../../../../../utils/helpers';
import { UiButtonComponent } from '../../../../../components/button/button.component';

// Interface pour l'état du stepper
export interface StepperState {
  activeStepId: string;
  completedSteps: string[];
  showPmpStep: boolean;
}

@Component({
  selector: 'app-add',
  imports: [
    FormsModule,
    RouterLink,
    SteponeactivitiesComponent,
    SteptwoactivitiesComponent,
    StepthreeactivitiesComponent,
    StepfouractivitiesComponent,
    CommonModule,
    RecapactivitiesComponent,
    UiButtonComponent,
  ],
  templateUrl: './add.component.html',
  styleUrl: './add.component.css'
})
export class AddActivityComponent implements OnInit, OnDestroy {

  // ─── Étapes de base (l'étape PMP est insérée dynamiquement) ───
  baseSteps = [
    { id: 'generales', label: 'Informations Générales' },
    { id: 'activites', label: "Informations sur l'Activité" },
    { id: 'budgets',   label: "Budget de l'activité" },
    { id: 'recap',     label: 'Récapitulatif' }
  ];

  steps: { id: string; label: string }[] = [];

  activeStepId: string = '';
  activity: any = {
    generales: { typeInterventionSelect: '', typeActivitySelect: '' },
    activites: {},
    budgets: {},
    stepfour: {}
  };

  public isLoading: boolean = false;
  public showPmpStep: boolean = false;
  public completedSteps: string[] = [];

  private isTypeNouvelleActivite: boolean = false;
  private isModePmp: boolean = false;

  private queryParamsSubscription: Subscription | undefined;
  private readonly stepperStateKey = 'activityStepperState';
  private readonly activityDataKey  = 'activity';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private activiteService: ActiviteService,
    private appService: AppService
  ) {}

  // ═══════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════

  ngOnInit(): void {
    // 1. Restaurer les données de l'activité depuis localStorage
    const storedActivity = localStorage.getItem(this.activityDataKey);
    if (storedActivity) {
      this.activity = JSON.parse(storedActivity);
      this.isTypeNouvelleActivite =
        this.activity.generales?.typeActivitySelect === 'Nouvelle activité';
      this.isModePmp = this.isTypeNouvelleActivite && this.isModePmp;
    } else {
      this.activity = {
        generales: { typeInterventionSelect: '', typeActivitySelect: '' },
        activites: {},
        budgets: {},
        stepfour: {}
      };
    }

    // 2. Restaurer l'état du stepper depuis localStorage
    const storedStepperState = localStorage.getItem(this.stepperStateKey);
    if (storedStepperState) {
      const state: StepperState = JSON.parse(storedStepperState);
      this.activeStepId    = state.activeStepId;
      this.completedSteps  = state.completedSteps;
      this.showPmpStep     = this.isTypeNouvelleActivite && this.isModePmp;
    } else {
      this.activeStepId   = 'generales';
      this.completedSteps = [];
      this.showPmpStep    = false;
      this.saveStepperState();
    }

    // 3. Construire le tableau des étapes
    this.updateStepsArray();

    // 4. S'abonner aux query params pour la navigation par URL
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      const requestedStepId  = params['step'];
      const currentStepIndex = this.steps.findIndex(s => s.id === requestedStepId);

      const isRecapAvailable =
        requestedStepId === 'recap' &&
        ((!this.showPmpStep && this.completedSteps.includes('budgets')) ||
          (this.showPmpStep && this.completedSteps.includes('stepfour')));

      if (requestedStepId && currentStepIndex !== -1) {
        const isStepAvailable =
          this.completedSteps.includes(requestedStepId) ||
          requestedStepId === this.activeStepId ||
          isRecapAvailable;

        if (isStepAvailable) {
          this.activeStepId = requestedStepId;
        } else {
          this.router.navigate([], {
            queryParams: { step: this.activeStepId },
            relativeTo: this.route,
            replaceUrl: true
          });
        }
      } else {
        this.router.navigate([], {
          queryParams: { step: this.activeStepId },
          relativeTo: this.route,
          replaceUrl: true
        });
      }

      this.saveStepperState();
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
  }

  // ═══════════════════════════════════════════════
  // STEPPER — MÉTHODES UTILITAIRES (template)
  // ═══════════════════════════════════════════════

  /** Index 0-based de l'étape active (utilisé par le compteur "X / N") */
  getActiveStepIndex(): number {
    return this.steps.findIndex(s => s.id === this.activeStepId);
  }

  /** Label de l'étape active affiché sous le stepper */
  getCurrentStepLabel(): string {
    return this.steps.find(s => s.id === this.activeStepId)?.label ?? '';
  }

  /**
   * Pourcentage de progression (0–100) pour la barre en haut du stepper.
   * Vaut 100 % uniquement sur la dernière étape (recap).
   */
  getProgressPercent(): number {
    const total = this.steps.length;
    if (total <= 1) return 0;
    const index = this.getActiveStepIndex();
    return Math.round((index / (total - 1)) * 100);
  }

  // ═══════════════════════════════════════════════
  // STEPPER — GESTION DES ÉTAPES
  // ═══════════════════════════════════════════════

  /** Reconstruit le tableau des étapes (avec ou sans l'étape PMP) */
  private updateStepsArray(): void {
    this.steps = [...this.baseSteps];
    if (this.showPmpStep) {
      const recapIndex = this.steps.findIndex(s => s.id === 'recap');
      if (recapIndex !== -1) {
        this.steps.splice(recapIndex, 0, {
          id: 'stepfour',
          label: 'Informations Marché'
        });
      } else {
        this.steps.push({ id: 'stepfour', label: 'Informations Marché' });
      }
    }
  }

  /** Persiste l'état du stepper dans localStorage */
  private saveStepperState(): void {
    const state: StepperState = {
      activeStepId:   this.activeStepId,
      completedSteps: this.completedSteps,
      showPmpStep:    this.isTypeNouvelleActivite && this.isModePmp
    };
    localStorage.setItem(this.stepperStateKey, JSON.stringify(state));
  }

  // ═══════════════════════════════════════════════
  // COMMUNICATION PARENT ↔ ENFANTS
  // ═══════════════════════════════════════════════

  /** Reçoit les données d'une étape enfant, met à jour l'état et navigue vers la suivante */
  updateActivity(stepData: any, stepId: string): void {
    // Stocker les données de l'étape
    if (stepId === 'generales') {
      this.activity.generales = stepData;
    } else if (stepId === 'activites') {
      this.activity.activites = stepData;
    } else if (stepId === 'budgets') {
      this.activity.budgets = stepData;
    } else if (stepId === 'stepfour') {
      this.activity.stepfour = stepData;
    }

    localStorage.setItem(this.activityDataKey, JSON.stringify(this.activity));

    // Marquer l'étape comme complétée AVANT de naviguer
    if (!this.completedSteps.includes(stepId)) {
      this.completedSteps.push(stepId);
    }

    // Naviguer vers l'étape suivante
    const currentStepIndex = this.steps.findIndex(s => s.id === stepId);
    if (currentStepIndex !== -1 && currentStepIndex < this.steps.length - 1) {
      const nextStepId = this.steps[currentStepIndex + 1].id;
      this.activeStepId = nextStepId;
      this.router.navigate([], {
        queryParams: { step: nextStepId },
        relativeTo: this.route
      });
    } else if (currentStepIndex === this.steps.length - 1 && stepId !== 'recap') {
      this.activeStepId = 'recap';
      this.router.navigate([], {
        queryParams: { step: 'recap' },
        relativeTo: this.route
      });
    }

    this.saveStepperState();
  }

  /** Émis par SteponeactivitiesComponent — active/désactive la condition PMP */
  handleTypeActivityChange(isNouvelleActivite: boolean): void {
    this.isTypeNouvelleActivite = isNouvelleActivite;
    this.updatePmpStepVisibility();
  }

  /** Émis par SteptwoactivitiesComponent — active/désactive le mode PMP */
  handlePmpModeChange(isPmp: boolean): void {
    this.isModePmp = isPmp;
    this.updatePmpStepVisibility();
  }

  /** Gestion centralisée de la visibilité de l'étape PMP */
  private updatePmpStepVisibility(): void {
    const shouldShow = this.isTypeNouvelleActivite && this.isModePmp;
    if (this.showPmpStep === shouldShow) return;

    this.showPmpStep = shouldShow;
    this.updateStepsArray();

    // Rediriger si l'utilisateur était sur l'étape PMP qui vient d'être masquée
    if (!this.showPmpStep && this.activeStepId === 'stepfour') {
      this.activeStepId = 'budgets';
      this.activity.stepfour = {};
      localStorage.setItem(this.activityDataKey, JSON.stringify(this.activity));
      this.router.navigate([], {
        queryParams: { step: 'budgets' },
        relativeTo: this.route,
        replaceUrl: true
      });
    }

    this.saveStepperState();
  }

  // ═══════════════════════════════════════════════
  // SOUMISSION FINALE
  // ═══════════════════════════════════════════════

  /** Appelé par RecapactivitiesComponent via (submitFinalActivity) */
  handleFinalSubmit(): void {
    this.onSubmitActivity();
  }

  onSubmitActivity(): void {
    const caracteristiquesArray: { code: string; valeur: any }[] = [];

    const excludedFieldForActivites = ['structureAssociee', 'dateDebut', 'dateFin', 'type'];
    Object.keys(this.activity.activites).forEach(key => {
      if (!excludedFieldForActivites.includes(key)) {
        caracteristiquesArray.push({
          code:   toSnakeCaseUpper(key),
          valeur: this.activity.activites[key] ?? ''
        });
      }
    });

    if (
      this.activity.activites.structureAssociee &&
      Array.isArray(this.activity.activites.structureAssociee)
    ) {
      this.activity.activites.structureAssociee.forEach((structureId: string) => {
        caracteristiquesArray.push({ code: 'STRUCTASSOCIE', valeur: structureId ?? '' });
      });
    }

    Object.keys(this.activity.budgets).forEach(key => {
      caracteristiquesArray.push({
        code:   toSnakeCaseUpper(key),
        valeur: this.activity.budgets[key] ?? ''
      });
    });

    Object.keys(this.activity.stepfour).forEach(key => {
      caracteristiquesArray.push({
        code:   toSnakeCaseUpper(key),
        valeur: this.activity.stepfour[key] ?? ''
      });
    });

    caracteristiquesArray.push({
      code:   'TYPE_ACTIVITE',
      valeur: this.activity.generales.typeActivitySelect ?? ''
    });

    // Transformation des tâches
    const transformedTaches: any[] = [];
    const monthNames = [
      'JANVIER', 'FEVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN',
      'JUILLET', 'AOUT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DECEMBRE'
    ];

    if (this.activity.generales.taches && Array.isArray(this.activity.generales.taches)) {
      this.activity.generales.taches.forEach((tache: any) => {
        const tacheCaracteristiques: { code: string; valeur: any }[] = [];

        if (tache.poids !== undefined) {
          tacheCaracteristiques.push({ code: 'POIDS', valeur: tache.poids ?? '' });
        }

        if (tache.chronogrammes && Array.isArray(tache.chronogrammes)) {
          tache.chronogrammes.forEach((isScheduled: boolean, index: number) => {
            if (index < monthNames.length) {
              tacheCaracteristiques.push({
                code:   monthNames[index],
                valeur: isScheduled ? true : false
              });
            }
          });
        }

        transformedTaches.push({
          titre:           tache.titreTache ?? '',
          caracteristiques: tacheCaracteristiques
        });
      });
    }

    const trueSendingActivity = {
      parent:          this.activity.generales.typeInterventionSelect ?? '',
      titre:           this.activity.generales.activities ?? '',
      objectif:        this.activity.generales.description ?? '',
      typeActivite:    this.activity.generales.type ?? '',
      caracteristiques: caracteristiquesArray,
      taches:          transformedTaches,
      applicationId:   this.appService.getAppId(),
      dateDebut:       this.activity.generales.dateDebut ?? '',
      dateFin:         this.activity.generales.dateFin ?? ''
    };

    if (this.activity == null) {
      console.warn('Les données de l\'activité sont vides.');
      return;
    }

    this.isLoading = true;
    console.info('Creating activity:', trueSendingActivity);

    this.activiteService.create(trueSendingActivity as any).subscribe({
      next: () => {
        localStorage.removeItem(this.activityDataKey);
        localStorage.removeItem(this.stepperStateKey);
        this.isLoading = false;
        this.router.navigate([
          routeAlias.activities.home,
          routeAlias.activities.activity.home
        ]);
      },
      error: (error) => {
        console.error('Erreur lors de la création de l\'activité :', error);
        this.isLoading = false;
      }
    });
  }
}